import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Upload, ShieldAlert, CheckCircle2, Clock, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const STATUS_META = {
  valid:         { label: 'Valid',        cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  expiring_30d:  { label: 'Expiring 30d', cls: 'bg-amber-100 text-amber-800',    Icon: Clock },
  expiring_7d:   { label: 'Expiring 7d',  cls: 'bg-orange-100 text-orange-800',  Icon: Clock },
  expired:       { label: 'Expired',      cls: 'bg-red-100 text-red-800',        Icon: XCircle },
  missing:       { label: 'Missing',      cls: 'bg-slate-100 text-slate-600',    Icon: XCircle },
  pending_review:{ label: 'In review',    cls: 'bg-blue-100 text-blue-800',      Icon: Clock },
  overridden:    { label: 'Overridden',   cls: 'bg-violet-100 text-violet-800',  Icon: ShieldAlert },
};

const CATEGORIES = [
  { key: 'certification', label: 'Certification (CPR, BLS, RN)' },
  { key: 'medical',       label: 'Medical (TB, physical, immunization)' },
  { key: 'identity',      label: 'Identity (DL, passport)' },
  { key: 'authorization', label: 'Authorization (I-9, work permit)' },
  { key: 'training',      label: 'Training certificate' },
  { key: 'tax',           label: 'Tax form (W-4, state tax)' },
  { key: 'other',         label: 'Other' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Worker HR Documents page. Shows the compliance eligibility roll-up
 * plus every doc the worker owns, with an inline upload flow. Talks
 * only to /api/hris/me/*, never to any admin/agency route.
 */
export default function WorkerHrisPage() {
  const [state, setState] = useState({ profile: null, documents: [], eligibility: null });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [replaceDocId, setReplaceDocId] = useState(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    category: 'certification', subtype: '', display_name: '', expires_at: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, el] = await Promise.all([
        apiCall('/api/hris/me'),
        apiCall('/api/hris/me/eligibility-preview'),
      ]);
      setState({
        profile: me.profile,
        documents: me.documents || [],
        eligibility: el.eligibility,
      });
    } catch (e) {
      toast.error(e?.message || 'Could not load HR records');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!form.subtype.trim()) return toast.error('Subtype required (e.g. CPR)');
    if (!file) return toast.error('Please attach a PDF/JPG/PNG (max 15MB)');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', form.category);
      fd.append('subtype', form.subtype.trim().toUpperCase());
      fd.append('display_name', form.display_name || form.subtype);
      fd.append('expires_at', form.expires_at || '');
      if (replaceDocId) fd.append('replace_doc_id', replaceDocId);
      await apiCall('/api/hris/me/documents/upload-file', {
        method: 'POST',
        body: fd,
      });
      toast.success(replaceDocId ? 'New version submitted for review'
                                 : 'Document submitted for review');
      setForm({ category: 'certification', subtype: '', display_name: '', expires_at: '' });
      setFile(null);
      setReplaceDocId(null);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    }
    setUploading(false);
  };

  const startReplace = (d) => {
    setReplaceDocId(d._id);
    setFile(null);
    setForm({
      category: d.category || 'other',
      subtype: d.subtype || '',
      display_name: d.display_name || d.subtype || '',
      expires_at: '',
    });
    setTimeout(() => {
      document.querySelector('[data-testid="worker-hris-upload-submit"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const cancelReplace = () => {
    setReplaceDocId(null);
    setFile(null);
    setForm({ category: 'certification', subtype: '', display_name: '', expires_at: '' });
  };

  // Sort docs by status priority: action-needed rows float to the top
  const STATUS_RANK = { expired: 0, expiring_7d: 1, expiring_30d: 2, pending_review: 3, missing: 4, overridden: 5, valid: 6 };
  const sortedDocs = [...(state.documents || [])].sort((a, b) => {
    const ra = STATUS_RANK[a.status] ?? 99;
    const rb = STATUS_RANK[b.status] ?? 99;
    return ra - rb;
  });

  const elig = state.eligibility;

  return (
    <div className="space-y-4" data-testid="worker-hris-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" /> HR & Compliance
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Your certifications, IDs, and tax forms. Only you and platform Admin can see the files.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
          data-testid="worker-hris-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {elig && (
        <div
          className={`rounded-xl border p-3 ${
            elig.eligible ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
          }`}
          data-testid="worker-hris-eligibility"
        >
          <div className="flex items-center gap-2">
            {elig.eligible ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <ShieldAlert className="w-4 h-4 text-red-600" />
            )}
            <p className="text-sm font-semibold text-slate-900">
              {elig.eligible ? 'You meet the baseline compliance rules.' : 'Compliance action needed'}
            </p>
          </div>
          {elig.reasons?.length > 0 && (
            <ul className="mt-2 ml-6 list-disc text-xs text-slate-700 space-y-0.5">
              {elig.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          {elig.warnings?.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              {elig.warnings.length} document{elig.warnings.length === 1 ? '' : 's'} expiring soon — replace before they lapse.
            </p>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900 text-sm">
              {replaceDocId ? 'Replace document (new version)' : 'Submit a document'}
            </h3>
          </div>
          {replaceDocId && (
            <button
              type="button"
              onClick={cancelReplace}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid="worker-hris-upload-cancel-replace"
            >
              Cancel replace
            </button>
          )}
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" onSubmit={submitUpload}>
          <label className="flex flex-col gap-1">
            <span className="text-slate-600 text-xs font-semibold">Category</span>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="rounded-lg border border-slate-300 px-3 py-2"
              data-testid="worker-hris-upload-category"
            >
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-600 text-xs font-semibold">Subtype (e.g. CPR, TB, W-4)</span>
            <input
              type="text"
              value={form.subtype}
              onChange={(e) => setForm({ ...form, subtype: e.target.value })}
              className="rounded-lg border border-slate-300 px-3 py-2"
              data-testid="worker-hris-upload-subtype"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-600 text-xs font-semibold">Display name</span>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="e.g. American Heart CPR card"
              className="rounded-lg border border-slate-300 px-3 py-2"
              data-testid="worker-hris-upload-name"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-600 text-xs font-semibold">Expiration date</span>
            <input
              type="date"
              value={form.expires_at ? form.expires_at.slice(0, 10) : ''}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              className="rounded-lg border border-slate-300 px-3 py-2"
              data-testid="worker-hris-upload-expires"
            />
          </label>
          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="text-slate-600 text-xs font-semibold">Attach file (PDF, JPG or PNG · 15MB max)</span>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/jpg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1 file:text-slate-700 hover:file:bg-slate-200"
              data-testid="worker-hris-upload-file"
            />
            {file && (
              <span className="text-[11px] text-slate-500" data-testid="worker-hris-upload-selected">
                Selected: {file.name} · {(file.size / 1024).toFixed(1)} KB
              </span>
            )}
            <span className="text-[11px] text-slate-500">
              Bytes stream to Emergent Object Storage; the row locks into "In review" until an Admin approves.
            </span>
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              data-testid="worker-hris-upload-submit"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {replaceDocId ? 'Submit new version' : 'Submit for review'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900 text-sm">My documents</h3>
        </div>
        {loading && state.documents.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : state.documents.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500" data-testid="worker-hris-empty">
            No documents yet. Submit your certifications above so employers can book you.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sortedDocs.map((d) => {
              const st = d.status || 'pending_review';
              const meta = STATUS_META[st] || STATUS_META.pending_review;
              const Icon = meta.Icon;
              const needsAction = ['expired', 'expiring_7d', 'expiring_30d', 'missing'].includes(st);
              return (
                <li
                  key={d._id}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`worker-hris-doc-${d._id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {d.display_name || d.subtype}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {d.category} · {d.subtype}
                      {d.expires_at && <> · expires {fmtDate(d.expires_at)}</>}
                      {d.review_state && d.review_state !== 'approved' && (
                        <> · <span className="text-blue-600 font-semibold">{d.review_state}</span></>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {needsAction && (
                      <button
                        onClick={() => startReplace(d)}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700"
                        data-testid={`worker-hris-replace-${d._id}`}
                      >
                        <Upload className="w-3 h-3" /> Replace
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
